import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { DenialPreventionData } from '@/lib/types';

interface DenialPreventionChartProps {
  data: DenialPreventionData[];
}

export function DenialPreventionChart({ data }: DenialPreventionChartProps) {
  const chartData = useMemo(() => {
    return data.slice(-14); // Last 14 days
  }, [data]);

  return (
    <div className="glass-card-elevated p-4 h-[320px]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-sm">Denial Prevention</h3>
          <p className="text-xs text-muted-foreground">Prevented vs occurred denials</p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-success rounded" />
            <span className="text-muted-foreground">Prevented</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-destructive rounded" />
            <span className="text-muted-foreground">Occurred</span>
          </div>
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height="85%">
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="hsl(var(--border))" 
            vertical={false}
          />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis 
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '8px',
              fontSize: '12px',
            }}
            labelStyle={{ color: 'hsl(var(--foreground))' }}
          />
          <Legend 
            wrapperStyle={{ fontSize: '11px' }}
            iconSize={8}
          />
          <Bar 
            dataKey="prevented" 
            name="Prevented"
            fill="hsl(var(--success))" 
            radius={[4, 4, 0, 0]}
            maxBarSize={24}
          />
          <Bar 
            dataKey="occurred" 
            name="Occurred"
            fill="hsl(var(--destructive))" 
            radius={[4, 4, 0, 0]}
            maxBarSize={24}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
